/** @jsxImportSource @emotion/react */
import styled from "@emotion/styled";
import { Table } from "@tanstack/react-table";
import { Spacer } from "components/general/Spacer/Spacer";
import { useIsMobile } from "hooks/useDeviceType";
import React, { useEffect } from "react";
import { TransactionDetail } from "types/history";
import { Chain } from "wagmi";
import { Filter, FiltersInterface } from "../../config/consts";
import { useTradeHistoryFilterToggle } from "../../hooks/useTradeHistoryFilterToggle";
import { FilterDropdownButton } from "./FilterDropdownButton";
import { FilterOptionRow } from "./elements/FilterOptionRow";
import { SUPPORTED_CHAINS } from "config/chains";

export interface NetworkFilterProps {
  table: Table<TransactionDetail>;
  confirmedOptions: FiltersInterface[Filter.Network];
  setConfirmedOptions: (updatedOptions: FiltersInterface[Filter.Network]) => void;
}
export const NetworkFilter: React.FC<NetworkFilterProps> = ({
  confirmedOptions,
  setConfirmedOptions,
  table,
}) => {
  const { localOptions, setLocalOptions, toggleLocalOptions } =
    useTradeHistoryFilterToggle<Chain["name"]>();
  const isMobile = useIsMobile();
  const iconSize = isMobile ? 24 : 22;

  useEffect(() => {
    setLocalOptions(confirmedOptions);
  }, [confirmedOptions, setLocalOptions]);

  return (
    <FilterDropdownButton
      filterType={Filter.Network}
      localOptions={localOptions}
      setLocalOptions={setLocalOptions}
      confirmedOptions={confirmedOptions}
      setConfirmedOptions={setConfirmedOptions}
      table={table}
    >
      <DropdownContainer>
        {SUPPORTED_CHAINS.sort((networkA, networkB) => {
          if (networkA == null || networkB == null) {
            return networkA.id.toString().localeCompare(networkB.id.toString());
          }
          return networkA.name.localeCompare(networkB.name);
        }).map(network => {
          const Icon = network.icon;
          return (
            <FilterOptionRow
              key={network.id}
              onClick={() => toggleLocalOptions(network.displayName)}
              checked={localOptions?.includes(network.displayName) ?? false}
            >
              <Content>
                <Icon width={iconSize} height={iconSize} color={network.iconColor} />
                <Spacer width="8px" />
                {network.displayName}
              </Content>
            </FilterOptionRow>
          );
        })}
      </DropdownContainer>
    </FilterDropdownButton>
  );
};

// Sub-components

const DropdownContainer = styled.div`
  min-width: 234px;
`;

const Content = styled.div`
  background: transparent;
  border: 0;
  display: flex;
  align-items: center;
`;
